// API client for the CRM backend
// Supports both local development (/api/local) and production (/api/v1)

// Configuration
const config = {
  // In development, proxy handles /api/v1 -> backend
  // In production on Cloudflare Pages, /api/v1 routes to Pages Functions
  apiBase: '/api/v1',
  // API key for authenticated requests (set via localStorage or environment)
  apiKey: typeof window !== 'undefined' ? localStorage.getItem('crm_api_key') : null,
  // Default tenant for ASI Construct production
  tenantId: 'TNT-1769685549',
};

// Helper to get config values (allows runtime updates)
export function getApiConfig() {
  return {
    ...config,
    apiKey: typeof window !== 'undefined' ? localStorage.getItem('crm_api_key') : null,
    tenantId: typeof window !== 'undefined'
      ? localStorage.getItem('crm_tenant_id') || config.tenantId
      : config.tenantId,
  };
}

// Set API key (call this after user authentication)
export function setApiKey(key: string | null) {
  if (typeof window !== 'undefined') {
    if (key) {
      localStorage.setItem('crm_api_key', key);
    } else {
      localStorage.removeItem('crm_api_key');
    }
  }
}

// Set tenant ID (for multi-tenant support)
export function setTenantId(tenantId: string | null) {
  if (typeof window !== 'undefined') {
    if (tenantId) {
      localStorage.setItem('crm_tenant_id', tenantId);
    } else {
      localStorage.removeItem('crm_tenant_id');
    }
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  skipAuth?: boolean; // For public endpoints
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, params, skipAuth = false } = options;
  const { apiBase, apiKey, tenantId } = getApiConfig();

  let url = `${apiBase}${endpoint}`;

  // Add query params
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add API key if available and not skipping auth
  if (apiKey && !skipAuth) {
    headers['X-API-Key'] = apiKey;
  }

  // Add tenant ID if available
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));

    // Handle specific error codes
    if (response.status === 401) {
      // Unauthorized - clear API key and redirect to login if needed
      setApiKey(null);
      throw new Error('Authentication required');
    }
    if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// Helper to add tenant_id to params
function withTenant(params?: Record<string, string | number | undefined>) {
  const { tenantId } = getApiConfig();
  return { tenant_id: tenantId, ...params };
}

// Helper to add tenant_id to body
function withTenantBody<T extends object>(data: T): T & { tenant_id: string } {
  const { tenantId } = getApiConfig();
  return { tenant_id: tenantId, ...data };
}

// Lead profile response type
export interface LeadProfile {
  lead: unknown;
  profile: {
    has_profile: boolean;
    markdown: string | null;
    quality_score: number | null;
    website: string | null;
    industry: string | null;
    enriched_at: string | null;
  };
}

// Leads API
export const leadsApi = {
  list: (filters?: { status?: string; source?: string; callbacks?: 'true'; limit?: number; offset?: number }) =>
    api<{ leads: unknown[]; count: number }>('/leads', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/leads/${id}`),

  getProfile: (id: string) => api<LeadProfile>(`/leads/${id}/profile`),

  create: (data: object) =>
    api<unknown>('/leads', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/leads/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/leads/${id}`, { method: 'DELETE' }),

  checkDuplicate: (email?: string, phone?: string) =>
    api<{ is_duplicate: boolean; duplicate_of: unknown }>('/leads/check-duplicate', {
      method: 'POST',
      body: withTenantBody({ email, phone }),
    }),

  convertToDeal: (id: string) =>
    api<{ deal: unknown; lead_id: string }>(`/leads/${id}/convert`, {
      method: 'POST',
    }),
};

// Deals API
export const dealsApi = {
  list: (filters?: { stage?: string; limit?: number; offset?: number }) =>
    api<{ deals: unknown[]; count: number }>('/deals', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/deals/${id}`),

  create: (data: object) =>
    api<unknown>('/deals', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/deals/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/deals/${id}`, { method: 'DELETE' }),

  stale: (daysThreshold?: number) =>
    api<{ deals: unknown[]; count: number }>('/deals/stale/list', {
      params: withTenant({ days_threshold: daysThreshold }),
    }),
};

// Contacts API
export const contactsApi = {
  list: (filters?: { limit?: number; offset?: number; search?: string }) =>
    api<{ contacts: unknown[]; count: number }>('/contacts', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/contacts/${id}`),

  create: (data: object) =>
    api<unknown>('/contacts', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/contacts/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),

  findByEmail: (email: string) =>
    api<unknown>('/contacts/find/by-email', {
      params: withTenant({ email }),
    }),
};

// Tasks API
export const tasksApi = {
  list: (filters?: { status?: string; priority?: string; limit?: number; offset?: number }) =>
    api<{ tasks: unknown[]; count: number }>('/tasks', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/tasks/${id}`),

  create: (data: object) =>
    api<unknown>('/tasks', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/tasks/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/tasks/${id}`, { method: 'DELETE' }),

  complete: (id: string) =>
    api<unknown>(`/tasks/${id}/complete`, { method: 'POST' }),

  overdue: () =>
    api<{ tasks: unknown[]; count: number }>('/tasks/overdue/list', {
      params: withTenant(),
    }),

  today: () =>
    api<{ tasks: unknown[]; count: number }>('/tasks/today/list', {
      params: withTenant(),
    }),
};

// Activities API
export const activitiesApi = {
  list: (entityType: string, entityId: string, filters?: { activity_type?: string; limit?: number }) =>
    api<{ activities: unknown[]; count: number }>('/activities', {
      params: { entity_type: entityType, entity_id: entityId, ...filters },
    }),

  create: (data: { entity_type: string; entity_id: string; activity_type: string; content?: string; metadata?: object }) =>
    api<unknown>('/activities', { method: 'POST', body: withTenantBody(data) }),
};

// Calls API
export const callsApi = {
  list: (filters?: { contact_id?: string; deal_id?: string; status?: string; limit?: number }) =>
    api<{ calls: unknown[]; count: number }>('/calls', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/calls/${id}`),

  getFull: (id: string) =>
    api<{ call: unknown; transcript: unknown; analysis: unknown }>(`/calls/${id}/full`),

  create: (data: object) =>
    api<unknown>('/calls', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/calls/${id}`, { method: 'PATCH', body: data }),

  getTranscript: (id: string) => api<unknown>(`/calls/${id}/transcript`),

  storeTranscript: (id: string, data: { full_text: string; segments?: unknown[]; provider?: string }) =>
    api<unknown>(`/calls/${id}/transcript`, { method: 'POST', body: data }),

  getAnalysis: (id: string) => api<unknown>(`/calls/${id}/analysis`),
};

// Proposals API
export const proposalsApi = {
  list: (filters?: { deal_id?: string; status?: string; limit?: number }) =>
    api<{ proposals: unknown[]; count: number }>('/proposals', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/proposals/${id}`),

  create: (data: object) =>
    api<unknown>('/proposals', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/proposals/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/proposals/${id}`, { method: 'DELETE' }),

  active: () =>
    api<{ proposals: unknown[]; count: number }>('/proposals/active/list', {
      params: withTenant(),
    }),
};

// Campaigns API
export const campaignsApi = {
  list: (filters?: { status?: string; campaign_type?: string; limit?: number; offset?: number }) =>
    api<{ campaigns: unknown[]; count: number }>('/campaigns', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/campaigns/${id}`),

  stats: () =>
    api<unknown>('/campaigns/stats', { params: withTenant() }),

  create: (data: object) =>
    api<unknown>('/campaigns', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/campaigns/${id}`, { method: 'PATCH', body: data }),

  delete: (id: string) => api<void>(`/campaigns/${id}`, { method: 'DELETE' }),
};

// Content API
export const contentApi = {
  list: (filters?: { content_type?: string; status?: string; pillar?: string; limit?: number; offset?: number }) =>
    api<{ content: unknown[]; count: number }>('/content', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/content/${id}`),

  getBySlug: (slug: string) =>
    api<unknown>(`/content/slug/${slug}`, { params: withTenant() }),

  stats: () =>
    api<unknown>('/content/stats', { params: withTenant() }),

  create: (data: object) =>
    api<unknown>('/content', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/content/${id}`, { method: 'PATCH', body: data }),

  publish: (id: string, url?: string) =>
    api<unknown>(`/content/${id}/publish`, { method: 'POST', body: { url } }),

  delete: (id: string) => api<void>(`/content/${id}`, { method: 'DELETE' }),
};

// Social Posts API
export const socialPostsApi = {
  list: (filters?: { platform?: string; status?: string; limit?: number; offset?: number }) =>
    api<{ posts: unknown[]; count: number }>('/social-posts', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/social-posts/${id}`),

  stats: () =>
    api<unknown>('/social-posts/stats', { params: withTenant() }),

  scheduled: () =>
    api<{ posts: unknown[]; count: number }>('/social-posts/scheduled', {
      params: withTenant(),
    }),

  due: () =>
    api<{ posts: unknown[]; count: number }>('/social-posts/due', {
      params: withTenant(),
    }),

  create: (data: object) =>
    api<unknown>('/social-posts', { method: 'POST', body: withTenantBody(data) }),

  update: (id: string, data: unknown) =>
    api<unknown>(`/social-posts/${id}`, { method: 'PATCH', body: data }),

  markPublished: (id: string, platform_post_id: string, platform_url?: string) =>
    api<unknown>(`/social-posts/${id}/published`, {
      method: 'POST',
      body: { platform_post_id, platform_url },
    }),

  markFailed: (id: string, error_message: string) =>
    api<unknown>(`/social-posts/${id}/failed`, {
      method: 'POST',
      body: { error_message },
    }),

  delete: (id: string) => api<void>(`/social-posts/${id}`, { method: 'DELETE' }),
};

export interface FunnelCheckpoint {
  status: 'not_published' | 'not_due' | 'unavailable' | 'collected';
  due_at?: string;
  metric_date?: string;
  views?: number;
  impressions?: number;
  reach?: number;
  engagements?: number;
  clicks?: number;
}

export interface FunnelPost {
  id: string;
  platform: string;
  status: string;
  published_at: string | null;
  platform_url: string | null;
  measurement_status: 'awaiting_publication' | 'missing_identity' | 'awaiting_collection' | 'collected';
  latest: ({ metric_date: string } & FunnelMetricValues) | null;
  journey: FunnelJourney;
  funnel: FunnelConversions;
  checkpoints: Record<'24h' | '7d' | '30d', FunnelCheckpoint>;
}

export interface FunnelJourney {
  site_visits: number;
  assessment_starts: number;
  assessment_completions: number;
}

export interface FunnelMetricValues {
  views: number;
  impressions: number;
  reach: number;
  engagements: number;
  clicks: number;
}

export interface FunnelConversions {
  leads: number;
  bookings: number;
  qualified_deals: number;
  wins: number;
  won_revenue: number;
}

export interface FunnelReport {
  generated_at: string;
  campaign: { id: string; name: string; status: string };
  availability: {
    platform_metrics: string;
    site_visits: string;
    direct_messages: string;
    assessment_progress: string;
    revenue: string;
  };
  totals: FunnelMetricValues & FunnelConversions & FunnelJourney & {
    published_posts: number;
    draft_posts: number;
  };
  content: Array<{
    id: string;
    title: string;
    status: string;
    posts: FunnelPost[];
    totals: FunnelMetricValues & FunnelConversions & FunnelJourney;
  }>;
}

export const analyticsApi = {
  funnel: (campaign = 'asi_foundations_launch') =>
    api<FunnelReport>('/analytics/funnel', { params: withTenant({ campaign }) }),
};

// Dashboard API
export const dashboardApi = {
  stats: () => api<unknown>('/dashboard/stats', { params: withTenant() }),
  pipeline: () => api<unknown>('/dashboard/pipeline', { params: withTenant() }),
  activities: (limit?: number) =>
    api<{ activities: unknown[] }>('/dashboard/activities', {
      params: withTenant({ limit }),
    }),
};

// Emails API
export const emailsApi = {
  list: (filters?: {
    contact_id?: string;
    deal_id?: string;
    status?: string;
    direction?: string;
    from_date?: string;
    to_date?: string;
    limit?: number;
    offset?: number;
  }) =>
    api<{ emails: unknown[]; count: number; total: number; limit: number; offset: number }>('/emails', {
      params: withTenant(filters),
    }),

  get: (id: string) => api<unknown>(`/emails/${id}`),

  stats: (filters?: { from_date?: string; to_date?: string }) =>
    api<{
      total_sent: number;
      delivered: number;
      opened: number;
      clicked: number;
      bounced: number;
      failed: number;
      delivery_rate: number;
      open_rate: number;
      click_rate: number;
      bounce_rate: number;
    }>('/emails/stats', { params: withTenant(filters) }),

  statsTimeline: (days?: number) =>
    api<{
      timeline: { date: string; sent: number; delivered: number; opened: number; clicked: number; bounced: number }[];
      days: number;
      from_date: string;
    }>('/emails/stats/timeline', { params: withTenant({ days }) }),

  awaitingResponse: (limit?: number) =>
    api<{
      contacts: {
        id: string;
        name: string;
        email: string | null;
        company: string | null;
        last_email_at: string;
        email_count: number;
        days_waiting: number;
        last_email_subject: string | null;
        urgency: 'low' | 'medium' | 'high' | 'critical';
      }[];
      count: number;
    }>('/emails/awaiting-response', { params: withTenant({ limit }) }),
};

// Prospects API (Construction Directory)
export interface Prospect {
  id: number;
  name: string;
  address: string | null;
  email: string | null;
  website: string | null;
  phone: string | null;
  tagline: string | null;
  rating: number | null;
  review_count: number | null;
  search_term: string | null;
  contact_status: 'not_contacted' | 'emailed' | 'called' | 'meeting_scheduled' | 'converted' | 'not_interested';
  last_contact_at: string | null;
  notes: string | null;
}

export const prospectsApi = {
  list: (filters?: {
    search?: string;
    search_term?: string;
    contact_status?: string;
    has_email?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api<{ prospects: Prospect[]; count: number; limit: number; offset: number }>('/prospects', {
      params: {
        ...filters,
        has_email: filters?.has_email ? 'true' : undefined,
      },
    }),

  get: (id: number) => api<Prospect>(`/prospects/${id}`),

  categories: () =>
    api<{ categories: { search_term: string; count: number }[] }>('/prospects/categories'),

  stats: () =>
    api<{
      total: number;
      with_email: number;
      contacted: number;
      converted: number;
      by_status: Record<string, number>;
    }>('/prospects/stats'),

  update: (id: number, data: { contact_status?: string; notes?: string }) =>
    api<Prospect>(`/prospects/${id}`, { method: 'PATCH', body: data }),

  bulkUpdate: (ids: number[], contact_status: string) =>
    api<{ updated: number }>('/prospects/bulk-update', {
      method: 'POST',
      body: { ids, contact_status },
    }),

  convert: (id: number) =>
    api<{ lead_id: string; contact_id: string; prospect_id: number }>(`/prospects/${id}/convert`, {
      method: 'POST',
      body: withTenantBody({}),
    }),
};
